<template>
  <div class="nx-data-grid-container" ref="dataGridContainer">
    <div class="nx-data-grid">
      <div v-show="false"><slot /></div>
      <table>
        <thead class="nx-data-grid-thead">
          <tr>
            <NxDataGridColumn
              v-for="(component, index) in $slots.default.filter(component => component.tag && component.tag.includes('NxDataGridColumn'))"
              :key="`column-${index}`"
              :title="component.componentOptions.propsData.title"
              :sortable="component.componentOptions.propsData.sortable"
              :field="component.componentOptions.propsData.field"
              :size="component.componentOptions.propsData.size"
              :sort="getSort(component.componentOptions.propsData.field)"
            />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in data"
            :key="`row-${rowIndex}`"
          >
            <td
              v-for="(component, columnIndex) in $slots.default.filter(component => component.tag && component.tag.includes('NxDataGridColumn'))"
              :key="`row-${rowIndex}-column-${columnIndex}`"
              @click="onClick($event, rowIndex, columnIndex, component.componentOptions.propsData.field)"
              @dblclick="onDoubleClick($event, rowIndex, columnIndex, component.componentOptions.propsData.field)"
            >
              <NxDataGridRow
                v-model="row[component.componentOptions.propsData.field]"
                :node="component.componentOptions.children"
                :item="row"
                :format="component.componentOptions.propsData.format"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <NxDataGridPager
      v-if="settings.showPager"
      class="mt-2"
      v-model="pageIndex"
      :totalPages="totalPages"
    />
  </div>
</template>

<script>
// Child components
import NxDataGridColumn from './NxDataGridColumn.vue'
import NxDataGridRow from './NxDataGridRow.vue'
import NxDataGridPager from './NxDataGridPager.vue'

// Mixins
import {
  usePageMixin,
  useScrollListenerMixin,
  useEventBuilderMixin } from '../../mixins'

// Constants
import { EVENT_NAME_CLICK, EVENT_NAME_DOUBLE_CLICK } from '../../constants/events'
import { PAGE_MODE_PAGE, PAGE_MODE_SCROLL } from '../../constants/pageModes'
import { SORT_TYPE_NONE } from '../../constants/sorts'

const scrollListenerMixin = useScrollListenerMixin(
  'dataGridContainer',
  {
    scrollUp: 'handleScrollUp',
    scrollDown: 'handleScrollDown'
  },
  false)

export default {
  name: 'NxDataGrid',
  components: {
    NxDataGridColumn,
    NxDataGridRow,
    NxDataGridPager
  },
  mixins: [
    scrollListenerMixin,
    usePageMixin,
    useEventBuilderMixin
  ],
  props: {
    mode: {
      type: String,
      default: 'page',
      required: false
    }
  },
  data () {
    return {
      settings: {
        showPager: null
      }
    }
  },
  computed: {
    isScrollMode () {
      return this.mode.toUpperCase() === PAGE_MODE_SCROLL.toUpperCase()
    },
    isPageMode () {
      return this.mode.toUpperCase() === PAGE_MODE_PAGE.toUpperCase()
    }
  },
  watch: {
    mode (newValue, oldValue) {
      if (oldValue.toUpperCase() === PAGE_MODE_SCROLL.toUpperCase()) {
        this.removeDataGridContainerScrollListener()
      }

      if (newValue.toUpperCase() === PAGE_MODE_SCROLL.toUpperCase()) {
        this.addDataGridContainerScrollListener()
      }

      this.setupMode()
    }
  },
  created () {
    this.setupMode()
  },
  beforeMount () {
    this.setupDefaultSorts()
  },
  mounted () {
    if (this.isScrollMode) {
      this.addDataGridContainerScrollListener()
    }
  },
  beforeDestroy () {
    if (this.isScrollMode) {
      this.removeDataGridContainerScrollListener()
    }
  },
  methods: {
    setupDefaultSorts () {
      this.$slots.default
        .filter(component => component.tag && component.tag.includes('NxDataGridColumn'))
        .map(component => {
          if (component.componentOptions.propsData.hasOwnProperty('field') &&
            component.componentOptions.propsData.hasOwnProperty('sort')) {
            this.handleSort(component.componentOptions.propsData.field, component.componentOptions.propsData.sort)
          }
        })
    },
    getSort (field) {
      return this.sort && this.sort.field === field ? this.sort.type : SORT_TYPE_NONE
    },
    setupMode () {
      this.isScrollMode ? this.useScrollMode() : this.usePageMode()
    },
    usePageMode () {
      this.settings.showPager = true
    },
    useScrollMode () {
      this.settings.showPager = false
    },
    async handleScrollUp () { },
    async handleScrollDown () {
      if (!this.isLoading && this.totalPages > 0 && this.pageIndex < this.totalPages) {
        await this.next()
      }
    },
    onClick (nativeEvent, index, columnIndex, fieldName) {
      const eventOptions = {
        nativeEvent,
        index
      }

      if (index >= 0) {
        eventOptions.item = this.data[index]

        if (fieldName) {
          eventOptions.columnClick = {
            index: columnIndex,
            field: fieldName,
            value: this.data[index][fieldName]
          }
        }
      }

      this.$emit(EVENT_NAME_CLICK, this.buildEvent(EVENT_NAME_CLICK, eventOptions))
    },
    onDoubleClick (nativeEvent, index, columnIndex, fieldName) {
      const eventOptions = {
        nativeEvent,
        index
      }

      if (index >= 0) {
        eventOptions.item = this.data[index]

        if (fieldName) {
          eventOptions.columnClick = {
            index: columnIndex,
            field: fieldName,
            value: this.data[index][fieldName]
          }
        }
      }

      this.$emit(EVENT_NAME_DOUBLE_CLICK, this.buildEvent(EVENT_NAME_DOUBLE_CLICK, eventOptions))
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/nexum/components/datagrid/NxDataGrid.vue